import { FileDropOverlay } from "@/components/FileDropOverlay";
import { ErrorComponent, InlineError, LoadingIndicator } from "@/components/QueryState";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { usePageFileDrop } from "@/hooks/usePageFileDrop";
import { api } from "@/lib/api";
import { fileToLogoDataUrl, LOGO_ACCEPT } from "@/lib/logo-file";
import { useOrgRole } from "@/lib/use-org-role";
import { OrgPage } from "@/pages/OrgPages";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { ImageUp } from "lucide-react";
import { useCallback, useRef, useState } from "react";

// ─── Settings content ────────────────────────────────────────────────────────
function OrgGeneralTab() {
  const { orgId, org, canManage, isLoading, isError, error } = useOrgRole();

  if (!orgId) return null;
  if (isLoading) return <LoadingIndicator />;
  if (isError || !org) return <ErrorComponent error={error ?? "Failed to load organization"} />;

  return (
    <div className="flex flex-col gap-8">
      <OrgProfileSection
        organizationId={orgId}
        currentName={org.name ?? ""}
        currentLogo={org.logo ?? null}
        canManage={canManage}
      />
    </div>
  );
}


// ─── Organization profile: logo and name ─────────────────────────────────────
function OrgProfileSection({
  organizationId,
  currentName,
  currentLogo,
  canManage,
}: {
  organizationId: string;
  currentName: string;
  currentLogo: string | null;
  canManage: boolean;
}) {
  const queryClient = useQueryClient();
  const [name, setName] = useState(currentName);
  const [logoError, setLogoError] = useState<string | null>(null);
  const fileInput = useRef<HTMLInputElement>(null);

  const [prevName, setPrevName] = useState(currentName);
  if (prevName !== currentName) {
    setPrevName(currentName);
    setName(currentName);
  }

  const invalidateOrg = useCallback(() => {
    queryClient.invalidateQueries({ queryKey: ["org-full", organizationId] });
    queryClient.invalidateQueries({ queryKey: ["orgs"] });
    // A super admin's sidebar names a foreign org from the admin lookup.
    queryClient.invalidateQueries({ queryKey: ["admin", "org", organizationId] });
  }, [queryClient, organizationId]);

  const rename = useMutation({
    mutationFn: (newName: string) => api.org.update({ name: newName }),
    onSuccess: () => {
      invalidateOrg();
      toast.success("Organization name saved");
    },
  });

  // `previous` is the logo being replaced; when set, the toast offers Undo.
  const updateLogo = useMutation({
    mutationFn: ({ logo }: { logo: string | null; previous?: string | null }) => api.org.update({ logo }),
    onSuccess: (_, { logo, previous }) => {
      invalidateOrg();
      const message = logo ? "Logo updated" : "Logo removed";
      if (previous === undefined) toast.success(message);
      else toast.success(message, { action: { label: "Undo", onClick: () => updateLogo.mutate({ logo: previous }) } });
    },
    onError: (err) => setLogoError(err instanceof Error ? err.message : "Could not save the logo"),
  });

  const setLogo = (logo: string | null) => updateLogo.mutate({ logo, previous: currentLogo });

  const pickLogo = async (file: File | undefined) => {
    if (!file) return;
    setLogoError(null);
    try {
      setLogo(await fileToLogoDataUrl(file));
    } catch (err) {
      setLogoError(err instanceof Error ? err.message : "Could not read that image");
    }
  };

  const dragging = usePageFileDrop({
    enabled: canManage && !updateLogo.isPending,
    onFiles: (files) => {
      if (files.length > 1) {
        setLogoError("Drop one image at a time.");
        return;
      }
      void pickLogo(files[0]);
    },
  });

  const trimmed = name.trim();
  const dirty = trimmed !== currentName && trimmed.length > 0;

  return (
    <section className="max-w-xl space-y-4">
      <FileDropOverlay
        show={dragging}
        icon={ImageUp}
        label={currentLogo ? "Drop to replace logo" : "Drop to upload logo"}
        hint="PNG, JPEG, WebP or SVG"
      />
      <h2 className="text-base font-semibold">Organization profile</h2>

      <div className="grid gap-2">
        <Label>Logo</Label>
        <div className="flex items-center gap-4">
          {canManage && !currentLogo ? (
            <button
              type="button"
              tabIndex={-1}
              onClick={() => fileInput.current?.click()}
              className="flex h-16 w-40 shrink-0 flex-col items-center justify-center gap-1 rounded-lg border border-dashed text-muted-foreground transition-colors hover:bg-muted/50 hover:text-foreground"
            >
              <ImageUp aria-hidden className="size-4" />
              <span className="text-xs">Drop an image</span>
            </button>
          ) : (
            <div className="flex h-16 w-40 shrink-0 items-center justify-center overflow-hidden rounded-lg border bg-white p-2">
              {currentLogo ? (
                <img src={currentLogo} alt={`${currentName} logo`} className="max-h-full max-w-full object-contain" />
              ) : (
                <span aria-hidden className="text-xl font-semibold text-muted-foreground">
                  {currentName.trim().charAt(0).toUpperCase() || "?"}
                </span>
              )}
            </div>
          )}
          <div className="flex flex-col gap-1.5">
            {canManage ? (
              <div className="flex gap-2">
                <input
                  ref={fileInput}
                  type="file"
                  accept={LOGO_ACCEPT}
                  className="sr-only"
                  onChange={(e) => {
                    void pickLogo(e.target.files?.[0]);
                    e.target.value = "";
                  }}
                />
                <Button variant="outline" size="sm" loading={updateLogo.isPending} onClick={() => fileInput.current?.click()}>
                  {currentLogo ? "Replace logo" : "Upload logo"}
                </Button>
                {currentLogo && (
                  <Button variant="ghost" size="sm" disabled={updateLogo.isPending} onClick={() => setLogo(null)}>
                    Remove logo
                  </Button>
                )}
              </div>
            ) : (
              <p className="text-xs text-muted-foreground">Only owners and admins can change the logo.</p>
            )}
            <p className="text-xs text-muted-foreground text-pretty">
              Shown with your organization name in the sidebar. PNG, JPEG, WebP or SVG, square or wide, at least 256px.
            </p>
            {logoError && <p className="text-xs text-destructive">{logoError}</p>}
          </div>
        </div>
      </div>

      <div className="grid gap-2">
        <Label htmlFor="org-name-edit">Organization name</Label>
        <div className="flex gap-2">
          <Input
            id="org-name-edit"
            value={name}
            onChange={(e) => setName(e.target.value)}
            readOnly={!canManage}
            className={canManage ? "" : "bg-muted text-muted-foreground"}
            onKeyDown={(e) => {
              if (e.key === "Enter" && canManage && dirty) rename.mutate(trimmed);
            }}
          />
          {canManage && (
            <Button onClick={() => rename.mutate(trimmed)} loading={rename.isPending} disabled={!dirty}>
              Save
            </Button>
          )}
        </div>
        {!canManage && <p className="text-xs text-muted-foreground">Only owners and admins can rename the organization.</p>}
        {rename.isError && <InlineError error={rename.error} />}
      </div>
    </section>
  );
}

// ─── Page ───────────────────────────────────────────────────────────────────
export function OrganizationSettingsPage() {
  return (
    <OrgPage title="Settings" subtitle="Your organization's name and logo.">
      <OrgGeneralTab />
    </OrgPage>
  );
}
